import store from "../store"
import { i18n } from "@/setups/i18n.js"
import { isCookieModeActive, cookieSessionExpired } from "@/modules/auth/cookieMode.js"
export function makeAxiosGraphQLFetcher({
	// Required
	url,
	axios,

	// Optional
	timeout = 60000,
	headers = {},

	// What is acceptable status code
	// Everything between 200 and 403 inclusive is ok for CoverGo APIs
	minResponseStatusInclusive = 200,
	maxResponseStatusInclusive = 403,

	// Mostly for development, will show queries, variables and headers in the console
	debug = false
}) {
	if (!axios) throw new Error("Axios dependency is required as a parameter")

	// Create axios instance for CoverGo GraphQL endpoint
	// Opinionated instance
	const coverGoGraphQL = axios.create({
		method: "post",
		timeout,

		// Default headers needed for GraphQL endpoint
		headers: {
			"Content-Type": "application/json",
			...headers
		},

		validateStatus: function(status) {
			return status >= minResponseStatusInclusive && status < maxResponseStatusInclusive // everything between 200 and 404 is ok for CoverGo APIs
		}
	})

	const refreshAccessToken = () => {
		return new Promise(resolve => {
			const query = `
				query refreshToken($refreshToken: String!) {
					refreshToken(tenantId: "${store.state.tenantId}", clientId: "${store.state.clientId}", refreshToken: $refreshToken) {
						accessToken
						refreshToken
					}
				}
			`

			const variables = { refreshToken: localStorage[process.env.VUE_APP_REFRESH_TOKEN_KEY] }
			coverGoGraphQL(typeof url === "function" ? url() : url, { data: { query, variables }, headers }).then(response => {
				const accessToken = response?.data?.data?.refreshToken?.accessToken
				const refreshToken = response?.data?.data?.refreshToken?.refreshToken
				localStorage.setItem(process.env.VUE_APP_TOKEN_KEY, accessToken)
				localStorage.setItem(process.env.VUE_APP_REFRESH_TOKEN_KEY, refreshToken)
				resolve(refreshToken)
			})
		})
	}

	const caller = ({
		query = null,
		variables = null,
		locale = store?.getters?.locale || "en-US",
		token = localStorage[process.env.VUE_APP_TOKEN_KEY]
	}) => {
		const headers = {}
		token = store.state.accessToken || localStorage[process.env.VUE_APP_TOKEN_KEY]
		if (locale) headers["Accept-Language"] = locale
		if (token) headers.Authorization = `Bearer ${token}` // append this header only if token exists otherwise can cause weird issues with API

		return new Promise((resolve, reject) => {
			const requestId = Math.random()
				.toString()
				.substr(2, 4)

			const logOut = () => {
				localStorage.removeItem(process.env.VUE_APP_TOKEN_KEY)
				localStorage.removeItem(process.env.VUE_APP_REFRESH_TOKEN_KEY)
				window.location = `/`
			}

			if (debug) {
				/* eslint-disable no-console */
				console.groupCollapsed(`%c CoverGo Request ${requestId}`, "padding: 1px 6px 1px 0px; background: #0256ab; color: white")
				console.log(query)
				console.log(JSON.stringify(variables))
				console.log(variables)
				console.log(JSON.stringify(headers))
				console.groupEnd()
				/* eslint-enable no-console */
			}

			const resolvedUrl = typeof url === "function" ? url() : url // if url is a function, we evaluate, otherwise use directly url string

			coverGoGraphQL(resolvedUrl, { data: { query, variables }, headers })
				.then(async response => {
					if (debug) {
						/* eslint-disable no-console */
						console.groupCollapsed(
							`%c CoverGo Response ${requestId}`,
							"padding: 1px 6px 1px 0px; color: #0256ab; background: white"
						)
						console.log(response)
						console.groupEnd()
						/* eslint-enable no-console */
					}
					// CH-37826: validateStatus accepts 200..402, so a 401 from the same-origin
					// auth_request gate resolves as SUCCESS and would fall through silently.
					// Gated, so the legacy path for every other host is unchanged.
					if (isCookieModeActive() && response?.status === 401) {
						cookieSessionExpired()
						return resolve(null)
					}

					if (
						response?.data?.errors?.find(
							error => error?.extensions?.code === "authorization" && error?.message?.includes("invalid or expired token")
						)
					) {
						// The BFF owns refresh in cookie mode: there is no refresh token in
						// localStorage, and the refreshToken query would 401 against the gate.
						if (isCookieModeActive()) {
							cookieSessionExpired()
							return resolve(null)
						}

						const refreshToken = await refreshAccessToken()

						if (!refreshToken) {
							logOut()
							showError()
							return resolve(null)
						}
						const retryData = await caller({ query, variables })
						resolve(retryData)
					}

					// Axios returns response.data.data, we want consumer to access directly response.data no response.data.data
					resolve(response?.data)
				})
				.catch(error => {
					// eslint-disable-next-line no-console
					console.error("Axios error during fetching", error)

					// Show error to users as some modules are not handling this properly so define it on global to avoid weird broken states without error message
					showError()

					// From Axios docs:
					// if (error.response) // The request was made and the server responded with a status code that falls out of the defined range
					// else if (error.request) The request was made but no response was received
					// else Something happened in setting up the request that triggered an Error

					// CoverGo expects fetcherErrorCodeResolver function that provides SERVER or NETWORK error codes
					// When error.status is undefined, it was most likely NETWORK error
					// When error.status is out of CoverGo defined range, it was most likely SERVER error
					// eslint-disable-next-line prefer-promise-reject-errors
					reject({
						fetcherErrorCodeResolver: () =>
							error?.status ? { code: "SERVER", message: error?.message } : { code: "NETWORK", message: error?.message }
					})
				})
		})
	}
	return caller
}

const showError = ({ code = "general", locale = store.getters.locale ?? "en-US" } = {}) => {
	store.dispatch("addToastMessage", {
		type: "error",
		content: {
			type: "message",
			text: i18n.messages[locale].global.errors[code]
		}
	})
}
